// src/components/sections/Blog.js
import React from 'react';
import { Calendar, Tag, ArrowRight, ExternalLink } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import { blogPosts } from '../../data/blogPosts';
import { motion } from 'framer-motion';

const Blog = ({ limit = 3 }) => {
  const { darkMode } = useTheme();
  const { language } = useLanguage();
  
  // Traductions
  const translations = {
    fr: {
      title: "Blog",
      subtitle: "Articles techniques et réflexions sur le développement",
      readMore: "Lire plus",
      viewAll: "Voir tous les articles",
      postedOn: "Publié le",
      minutes: "min de lecture",
      comingSoon: "Page complète bientôt disponible"
    },
    en: {
      title: "Blog",
      subtitle: "Technical articles and thoughts on development",
      readMore: "Read more",
      viewAll: "View all articles",
      postedOn: "Posted on",
      minutes: "min read",
      comingSoon: "Full page coming soon"
    }
  };
  
  const t = translations[language];
  
  // Récupérer les articles limités
  const limitedPosts = blogPosts.slice(0, limit);
  
  // Estimer le temps de lecture (1 min par 200 mots)
  const getReadTime = (content) => {
    const wordCount = content.split(/\s+/).length;
    return Math.ceil(wordCount / 200);
  };
  
  // Gestionnaire pour afficher une alerte lorsque l'utilisateur clique sur un lien qui n'est pas encore implémenté
  const handleComingSoon = (e) => {
    e.preventDefault();
    alert(t.comingSoon);
  };
  
  return (
    <section id="blog" className={`py-16 ${darkMode ? 'bg-gray-900' : 'bg-white'}`}>
      <div className="container mx-auto px-6">
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="text-3xl font-bold mb-4">{t.title}</h2>
          <p className={`max-w-2xl mx-auto ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            {t.subtitle}
          </p>
        </motion.div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {limitedPosts.map((post, index) => (
            <motion.div
              key={post.id}
              className={`rounded-lg overflow-hidden shadow-lg transition-transform duration-300 hover:-translate-y-2 ${
                darkMode ? 'bg-gray-800' : 'bg-white'
              }`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              {/* Image de couverture */}
              <a href="#" onClick={handleComingSoon} className="block">
                <img 
                  src={post.image} 
                  alt={post.title[language]} 
                  className="w-full h-48 object-cover"
                />
              </a>
              
              {/* Contenu */}
              <div className="p-6">
                {/* Catégorie et date */}
                <div className="flex justify-between items-center mb-4 text-sm">
                  <span className={`px-3 py-1 rounded-full ${
                    darkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-800'
                  }`}>
                    {post.category[language]}
                  </span>
                  <div className={`flex items-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    <Calendar className="w-4 h-4 mr-1" />
                    <span>{post.date[language]}</span>
                  </div>
                </div>
                
                {/* Titre */}
                <a href="#" onClick={handleComingSoon} className="block">
                  <h3 className={`text-xl font-bold mb-3 hover:underline ${
                    darkMode ? 'text-white' : 'text-gray-800'
                  }`}>
                    {post.title[language]}
                  </h3>
                </a>
                
                {/* Extrait */}
                <p className={`mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  {post.excerpt[language]}
                </p>
                
                {/* Tags */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {post.tags.slice(0, 3).map((tag, idx) => (
                    <div 
                      key={idx} 
                      className={`flex items-center text-xs ${
                        darkMode ? 'text-gray-400' : 'text-gray-500'
                      }`}
                    >
                      <Tag className="w-3 h-3 mr-1" />
                      {tag}
                    </div>
                  ))}
                  <div className={`flex items-center text-xs ${
                    darkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    <span>{getReadTime(post.content[language])} {t.minutes}</span>
                  </div>
                </div>
                
                {/* Lien pour lire plus */}
                <a 
                  href="#" 
                  onClick={handleComingSoon}
                  className={`inline-flex items-center mt-2 ${
                    darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'
                  }`}
                >
                  {t.readMore} <ArrowRight className="w-4 h-4 ml-2" />
                </a>
              </div>
            </motion.div>
          ))}
        </div>
        
        {limit < blogPosts.length && (
          <motion.div 
            className="text-center mt-12"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <a 
              href="#" 
              onClick={handleComingSoon}
              className={`inline-flex items-center px-6 py-3 rounded-lg transition-colors duration-200 ${
                darkMode 
                  ? 'bg-blue-700 hover:bg-blue-600 text-white' 
                  : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
            >
              {t.viewAll}
            </a>
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default Blog;